import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  CreditCard,
  FileText,
  DollarSign,
  Plus,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  Building2,
  Search,
  Eye,
  RefreshCw,
  Users,
  ChevronRight,
  ChevronLeft,
  Receipt,
  TrendingUp,
  Clock,
  Settings,
  Link,
  Unlink,
  Download,
  ExternalLink,
  Repeat,
  RotateCcw,
  Landmark,
} from 'lucide-react';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
import SubscriptionSection from './SubscriptionSection';
import RefundApprovalPanel from './RefundApprovalPanel';
import RefundList from './RefundList';
import RefundDetails from './RefundDetails';
import CreditNoteModal from './CreditNoteModal';
import AdminRefundModal from './AdminRefundModal';

/**
 * AdminBillingDashboard - Superadmin billing management
 *
 * Features:
 * - View all organizations with billing summary
 * - Create invoices for any organization
 * - Charge organization credit cards
 * - Manage subscriptions
 */
const AdminBillingDashboard = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);

  // Organizations data
  const [organizations, setOrganizations] = useState([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [orgPage, setOrgPage] = useState(1);
  const [orgTotalCount, setOrgTotalCount] = useState(0);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const orgItemsPerPage = 20;

  // Debounced search term for automatic searching
  const debouncedOrgSearch = useDebounce(orgSearch, 300);

  // Invoices data
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState({ organization_id: '', status: '' });

  // Receipts data
  const [receipts, setReceipts] = useState([]);
  const [billingSubTab, setBillingSubTab] = useState('invoices'); // 'invoices' or 'receipts'

  // Invoice creation
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [createInvoiceOrgId, setCreateInvoiceOrgId] = useState('');

  // Xero settings
  const [xeroSettings, setXeroSettings] = useState(null);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [xeroBankAccounts, setXeroBankAccounts] = useState([]);
  const [loadingXeroAccounts, setLoadingXeroAccounts] = useState(false);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  // Xero sync status
  const [pendingSyncSummary, setPendingSyncSummary] = useState(null);
  const [processingSync, setProcessingSync] = useState(false);

  // Invoice detail modal
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Refund state
  const [selectedRefundDetails, setSelectedRefundDetails] = useState(null);
  const [refundsRefreshKey, setRefundsRefreshKey] = useState(0);

  // Admin refund/credit note modals
  const [showAdminRefundModal, setShowAdminRefundModal] = useState(false);
  const [adminRefundInvoice, setAdminRefundInvoice] = useState(null);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteOrg, setCreditNoteOrg] = useState(null);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null);

  // Credit notes data
  const [creditNotes, setCreditNotes] = useState([]);
  const [loadingCreditNotes, setLoadingCreditNotes] = useState(false);

  // ACH Billing data
  const [achSummary, setAchSummary] = useState(null);
  const [achConfigs, setAchConfigs] = useState([]);
  const [achSelectedOrg, setAchSelectedOrg] = useState(null);
  const [achOrgEntries, setAchOrgEntries] = useState([]);
  const [achOrgCharges, setAchOrgCharges] = useState([]);
  const [achOrgBalance, setAchOrgBalance] = useState(null);
  const [loadingAch, setLoadingAch] = useState(false);
  const [loadingAchOrg, setLoadingAchOrg] = useState(false);

  // Load initial data
  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'organizations') {
      loadOrganizations();
    } else if (activeTab === 'invoices') {
      loadInvoices();
      loadReceipts();
    } else if (activeTab === 'settings') {
      loadXeroSettings();
      loadPendingSyncSummary();
    } else if (activeTab === 'credit-notes') {
      loadCreditNotes();
    } else if (activeTab === 'ach-billing') {
      loadAchSummary();
    }
  }, [activeTab]);

  // Auto-search when debounced search term or page changes
  useEffect(() => {
    if (activeTab === 'organizations') {
      loadOrganizations();
    }
  }, [debouncedOrgSearch, orgPage]);

  // Reset page when search changes
  useEffect(() => {
    setOrgPage(1);
  }, [orgSearch]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/dashboard`,
        { headers: getAuthHeaders() }
      );
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/organizations`,
        {
          headers: getAuthHeaders(),
          params: {
            search: debouncedOrgSearch || undefined,
            limit: orgItemsPerPage,
            offset: (orgPage - 1) * orgItemsPerPage
          },
        }
      );
      setOrganizations(response.data.organizations || []);
      setOrgTotalCount(response.data.total_count || response.data.organizations?.length || 0);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadOrgDetail = async (orgId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/organizations/${orgId}`,
        { headers: getAuthHeaders() }
      );
      setOrgDetail(response.data);
      setSelectedOrg(response.data.organization);
    } catch (err) {
      console.error('Error loading organization detail:', err);
      setError('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/invoices`,
        {
          headers: getAuthHeaders(),
          params: {
            organization_id: invoiceFilter.organization_id || undefined,
            status: invoiceFilter.status || undefined,
            limit: 100,
          },
        }
      );
      setInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices');
    }
  };

  const loadReceipts = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/receipts`,
        {
          headers: getAuthHeaders(),
          params: {
            organization_id: invoiceFilter.organization_id || undefined,
            limit: 100,
          },
        }
      );
      setReceipts(response.data.receipts || []);
    } catch (err) {
      console.error('Error loading receipts:', err);
      setError('Failed to load receipts');
    }
  };

  const loadCreditNotes = async () => {
    try {
      setLoadingCreditNotes(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/credit-notes`,
        {
          headers: getAuthHeaders(),
          params: { limit: 100 },
        }
      );
      setCreditNotes(response.data.credit_notes || []);
    } catch (err) {
      console.error('Error loading credit notes:', err);
      setError('Failed to load credit notes');
    } finally {
      setLoadingCreditNotes(false);
    }
  };

  const loadAchSummary = async () => {
    try {
      setLoadingAch(true);
      const [summaryRes, configsRes] = await Promise.all([
        axios.get(`${getApiBase()}/billing/ach/admin/summary`, { headers: getAuthHeaders() }).catch(() => null),
        axios.get(`${getApiBase()}/billing/ach/admin/configs?limit=100`, { headers: getAuthHeaders() }).catch(() => ({ data: { data: [] } })),
      ]);
      if (summaryRes?.data?.data) setAchSummary(summaryRes.data.data);
      setAchConfigs(configsRes?.data?.data || []);
    } catch (err) {
      console.error('Error loading ACH summary:', err);
    } finally {
      setLoadingAch(false);
    }
  };

  const loadAchOrgDetail = async (orgId) => {
    try {
      setLoadingAchOrg(true);
      const [balanceRes, entriesRes, chargesRes] = await Promise.all([
        axios.get(`${getApiBase()}/billing/ach/balance/${orgId}`, { headers: getAuthHeaders() }).catch(() => null),
        axios.get(`${getApiBase()}/billing/ach/balance/${orgId}/entries?limit=50`, { headers: getAuthHeaders() }).catch(() => ({ data: { data: [] } })),
        axios.get(`${getApiBase()}/billing/ach/charges/${orgId}?limit=50`, { headers: getAuthHeaders() }).catch(() => ({ data: { data: [] } })),
      ]);
      if (balanceRes?.data?.data) setAchOrgBalance(balanceRes.data.data);
      setAchOrgEntries(entriesRes?.data?.data || []);
      setAchOrgCharges(chargesRes?.data?.data || []);
    } catch (err) {
      console.error('Error loading ACH org detail:', err);
    } finally {
      setLoadingAchOrg(false);
    }
  };

  const downloadReceiptPdf = async (receiptId) => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/receipts/${receiptId}/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob',
        }
      );

      const contentDisposition = response.headers['content-disposition'];
      let filename = `receipt-${receiptId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading receipt PDF:', err);
      setError(err.response?.data?.detail || 'Failed to download receipt PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const loadXeroSettings = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/xero/settings`,
        { headers: getAuthHeaders() }
      );
      setXeroSettings(response.data);
    } catch (err) {
      console.error('Error loading Xero settings:', err);
      setError('Failed to load Xero settings');
    }
  };

  const loadXeroAccounts = async () => {
    try {
      setLoadingXeroAccounts(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/xero/accounts`,
        { headers: getAuthHeaders() }
      );
      setXeroAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error loading Xero accounts:', err);
      setError(err.response?.data?.detail || 'Failed to load Xero accounts');
    } finally {
      setLoadingXeroAccounts(false);
    }
  };

  const loadXeroBankAccounts = async () => {
    try {
      setLoadingBankAccounts(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/xero/bank-accounts`,
        { headers: getAuthHeaders() }
      );
      setXeroBankAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error loading Xero bank accounts:', err);
      setError(err.response?.data?.detail || 'Failed to load Xero bank accounts');
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const saveXeroSettings = async (accountCode, accountName, taxType, bankCode, bankName) => {
    try {
      setProcessing(true);
      const response = await axios.put(
        `${getApiBase()}/admin/billing/xero/settings`,
        {
          revenue_account_code: accountCode,
          revenue_account_name: accountName,
          tax_type: taxType,
          bank_account_code: bankCode,
          bank_account_name: bankName,
        },
        { headers: getAuthHeaders() }
      );
      setXeroSettings(response.data.settings);
      setSuccess('Xero settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving Xero settings:', err);
      setError(err.response?.data?.detail || 'Failed to save Xero settings');
    } finally {
      setProcessing(false);
    }
  };

  const loadPendingSyncSummary = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/xero/pending-syncs`,
        { headers: getAuthHeaders() }
      );
      setPendingSyncSummary(response.data);
    } catch (err) {
      console.error('Error loading pending sync summary:', err);
      // Don't show error - not critical
    }
  };

  const processPendingSyncs = async () => {
    try {
      setProcessingSync(true);
      const response = await axios.post(
        `${getApiBase()}/admin/billing/xero/process-pending`,
        null,
        { headers: getAuthHeaders() }
      );
      const results = response.data;
      setSuccess(
        `Processed ${results.processed} invoices: ${results.synced} synced, ` +
        `${results.retrying} retrying, ${results.failed} failed`
      );
      setTimeout(() => setSuccess(''), 5000);
      // Reload summary and invoices
      loadPendingSyncSummary();
      if (activeTab === 'invoices') {
        loadInvoices();
      }
    } catch (err) {
      console.error('Error processing pending syncs:', err);
      setError(err.response?.data?.detail || 'Failed to process pending syncs');
    } finally {
      setProcessingSync(false);
    }
  };

  const retryInvoiceSync = async (invoiceId) => {
    try {
      setProcessing(true);
      const response = await axios.post(
        `${getApiBase()}/admin/billing/invoices/${invoiceId}/retry-sync`,
        null,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        setSuccess('Invoice synced to Xero successfully');
      } else {
        setError(response.data.error || 'Sync failed, will retry later');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
      // Reload data
      loadInvoices();
      loadPendingSyncSummary();
      if (orgDetail) {
        loadOrgDetail(orgDetail.organization.id);
      }
    } catch (err) {
      console.error('Error retrying invoice sync:', err);
      setError(err.response?.data?.detail || 'Failed to retry sync');
    } finally {
      setProcessing(false);
    }
  };

  const loadInvoiceDetail = async (invoiceId) => {
    try {
      setLoadingInvoiceDetail(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/invoices/${invoiceId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedInvoice(response.data);
    } catch (err) {
      console.error('Error loading invoice detail:', err);
      setError(err.response?.data?.detail || 'Failed to load invoice details');
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const downloadInvoicePdf = async (invoiceId) => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/invoices/${invoiceId}/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `invoice-${invoiceId}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Invoice PDF downloaded');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      setError(err.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      issued: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      partially_paid: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      voided: 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Helper to display invoice number with source and sync status badges
  const InvoiceNumberDisplay = ({ invoice }) => {
    const number = invoice.invoice_number || invoice.provisional_number;
    const isProvisional = !invoice.invoice_number && invoice.provisional_number;
    const source = invoice.invoice_number_source;
    const syncStatus = invoice.xero_sync_status;

    // Get sync status badge
    const getSyncBadge = () => {
      if (syncStatus === 'pending' || syncStatus === 'retrying') {
        return (
          <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {syncStatus === 'retrying' ? 'Retrying' : 'Pending Sync'}
          </span>
        );
      }
      if (syncStatus === 'failed') {
        return (
          <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Sync Failed
          </span>
        );
      }
      return null;
    };

    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={isProvisional ? 'text-gray-500 italic' : 'font-medium'}>
          {number || '-'}
        </span>
        {isProvisional && !syncStatus && (
          <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
            Draft
          </span>
        )}
        {!isProvisional && source && (
          <span className={`px-1.5 py-0.5 text-xs rounded ${
            source === 'xero'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-purple-100 text-purple-700'
          }`}>
            {source === 'xero' ? 'Xero' : 'App'}
          </span>
        )}
        {getSyncBadge()}
      </div>
    );
  };

  // =========================================================================
  // Dashboard Section
  // =========================================================================

  const DashboardSection = () => {
    if (!dashboardData) return null;

    const stats = [
      {
        label: 'Organizations',
        value: dashboardData.organizations?.total || 0,
        subValue: `${dashboardData.organizations?.with_billing || 0} with billing`,
        icon: Building2,
        color: 'blue',
      },
      {
        label: 'Outstanding',
        value: formatCurrency(dashboardData.invoices?.total_outstanding || 0),
        subValue: `${dashboardData.invoices?.unpaid || 0} unpaid invoices`,
        icon: Receipt,
        color: 'yellow',
      },
      {
        label: 'Total Collected',
        value: formatCurrency(dashboardData.payments?.total_collected || 0),
        subValue: 'All time',
        icon: TrendingUp,
        color: 'green',
      },
      {
        label: 'Pending Actions',
        value: dashboardData.refunds?.pending || 0,
        subValue: 'Refunds pending approval',
        icon: Clock,
        color: 'purple',
      },
    ];

    const colorClasses = {
      blue: 'bg-blue-100 text-blue-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
    };

    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-semibold">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.subValue}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('invoices')}
              className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-8 h-8 text-blue-600 mr-4" />
              <div className="text-left">
                <p className="font-medium">View All Invoices</p>
                <p className="text-sm text-gray-500">{dashboardData.invoices?.total || 0} total</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </button>

            <button
              onClick={() => setActiveTab('organizations')}
              className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Building2 className="w-8 h-8 text-green-600 mr-4" />
              <div className="text-left">
                <p className="font-medium">Manage Organizations</p>
                <p className="text-sm text-gray-500">{dashboardData.organizations?.total || 0} organizations</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </button>

            <button
              onClick={() => { setShowCreateInvoice(true); setCreateInvoiceOrgId(''); }}
              className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-8 h-8 text-purple-600 mr-4" />
              <div className="text-left">
                <p className="font-medium">Create Invoice</p>
                <p className="text-sm text-gray-500">Bill an organization</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </button>
          </div>
        </div>

        {/* Draft Invoices */}
        {dashboardData.invoices?.draft > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
              <div>
                <p className="font-medium text-yellow-800">
                  {dashboardData.invoices.draft} draft invoice(s) pending
                </p>
                <p className="text-sm text-yellow-600">
                  Review and issue these invoices when ready.
                </p>
              </div>
              <button
                onClick={() => { setActiveTab('invoices'); setInvoiceFilter({ ...invoiceFilter, status: 'draft' }); }}
                className="ml-auto px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
              >
                View Drafts
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // Organizations Section
  // =========================================================================

  // Note: This is a render function (not a component) to avoid focus loss on re-render
  const renderOrganizationsSection = () => {
    // Organization detail view
    if (selectedOrg && orgDetail) {
      return (
        <div className="space-y-6">
          {/* Back button */}
          <button
            onClick={() => { setSelectedOrg(null); setOrgDetail(null); }}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Back to Organizations
          </button>

          {/* Organization Header */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedOrg.name}</h2>
                <p className="text-gray-500">{selectedOrg.id}</p>
              </div>
              <button
                onClick={() => { setShowCreateInvoice(true); setCreateInvoiceOrgId(selectedOrg.id); }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </button>
            </div>
          </div>

          {/* Billing Profile */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Billing Profile</h3>
            </div>
            <div className="p-6">
              {orgDetail.billing_profile ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Company</p>
                    <p className="font-medium">{orgDetail.billing_profile.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{orgDetail.billing_profile.billing_email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Stripe Customer</p>
                    <p className="font-medium text-xs font-mono">
                      {orgDetail.billing_profile.stripe_customer_id || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Default Payment Method</p>
                    <p className="font-medium">
                      {orgDetail.billing_profile.default_payment_method_id ? (
                        <span className="text-green-600 flex items-center">
                          <Check className="w-4 h-4 mr-1" /> Set
                        </span>
                      ) : (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No billing profile set up</p>
                  <p className="text-sm">Organization needs to create a billing profile first</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Methods */}
          {orgDetail.payment_methods && orgDetail.payment_methods.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">Payment Methods</h3>
              </div>
              <div className="p-6 space-y-3">
                {orgDetail.payment_methods.map((pm) => (
                  <div key={pm.id} className="flex items-center p-3 border rounded-lg">
                    <CreditCard className="w-6 h-6 text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium">
                        {pm.card_brand} ****{pm.card_last4}
                        {pm.is_default && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Default</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">Expires {pm.card_exp_month}/{pm.card_exp_year}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Invoices */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Recent Invoices</h3>
            </div>
            {orgDetail.recent_invoices && orgDetail.recent_invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orgDetail.recent_invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="px-6 py-4 text-sm">
                          <InvoiceNumberDisplay invoice={inv} />
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">{formatCurrency(inv.total)}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(inv.due_date)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {(inv.status === 'issued' || inv.status === 'partially_paid') && orgDetail.billing_profile?.default_payment_method_id && (
                              <button
                                onClick={() => handleChargeInvoice(inv.id)}
                                disabled={processing}
                                className="text-green-600 hover:text-green-800 font-medium text-sm"
                              >
                                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Charge Card'}
                              </button>
                            )}
                            {inv.status === 'issued' && !inv.xero_invoice_id &&
                             (inv.xero_sync_status === 'pending' || inv.xero_sync_status === 'retrying' || inv.xero_sync_status === 'failed') && (
                              <button
                                onClick={() => retryInvoiceSync(inv.id)}
                                disabled={processing}
                                className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                              >
                                Retry Sync
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No invoices yet</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Calculate pagination
    const totalPages = Math.ceil(orgTotalCount / orgItemsPerPage);

    // Organizations list view
    return (
      <div className="space-y-6">
        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations... (auto-search)"
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              {loadingOrgs && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" />
              )}
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Showing {organizations.length} of {orgTotalCount} organizations
            {orgSearch && ` matching "${orgSearch}"`}
          </div>
        </div>

        {/* Organizations List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Organizations</h3>
          </div>
          {loadingOrgs && organizations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="w-8 h-8 mx-auto mb-4 text-blue-500 animate-spin" />
              <p>Loading organizations...</p>
            </div>
          ) : organizations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>{orgSearch ? 'No organizations match your search' : 'No organizations found'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {organizations.map((item) => (
                <div
                  key={item.organization.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => loadOrgDetail(item.organization.id)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Building2 className="w-8 h-8 text-gray-400 mr-4" />
                      <div>
                        <p className="font-medium text-gray-900">{item.organization.name}</p>
                        <p className="text-sm text-gray-500">
                          {item.billing_profile ? item.billing_profile.billing_email : 'No billing profile'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {item.invoice_summary.unpaid_invoices > 0 ? (
                        <div>
                          <p className="font-medium text-yellow-600">
                            {formatCurrency(item.invoice_summary.total_outstanding)} outstanding
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.invoice_summary.unpaid_invoices} unpaid invoice(s)
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No outstanding balance</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 ml-4" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {orgPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOrgPage(prev => Math.max(1, prev - 1))}
                  disabled={orgPage === 1 || loadingOrgs}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (orgPage <= 3) {
                      pageNum = i + 1;
                    } else if (orgPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = orgPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setOrgPage(pageNum)}
                        disabled={loadingOrgs}
                        className={`px-3 py-1.5 rounded-md text-sm ${
                          orgPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setOrgPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={orgPage === totalPages || loadingOrgs}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Invoices & Receipts Section
  // =========================================================================

  const InvoicesSection = () => {
    return (
      <div className="space-y-6">
        {/* Sub-tabs for Invoices/Receipts */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b px-4">
            <nav className="flex space-x-6">
              <button
                onClick={() => setBillingSubTab('invoices')}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  billingSubTab === 'invoices'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Invoices ({invoices.length})
              </button>
              <button
                onClick={() => setBillingSubTab('receipts')}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  billingSubTab === 'receipts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Receipt className="w-4 h-4 inline mr-2" />
                Receipts ({receipts.length})
              </button>
            </nav>
          </div>
        </div>

        {billingSubTab === 'invoices' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={invoiceFilter.status}
                    onChange={(e) => setInvoiceFilter({ ...invoiceFilter, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="issued">Issued</option>
                    <option value="paid">Paid</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="voided">Voided</option>
                  </select>
                </div>
                <button
                  onClick={loadInvoices}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Filter
                </button>
                <button
                  onClick={() => { setShowCreateInvoice(true); setCreateInvoiceOrgId(''); }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </button>
              </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">All Invoices</h3>
                <button onClick={loadInvoices} className="text-gray-600 hover:text-gray-800 p-2">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              {invoices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No invoices found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Ref</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm">
                            <InvoiceNumberDisplay invoice={inv} />
                          </td>
                          <td className="px-6 py-4 text-sm">{inv.organization_name}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(inv.status)}`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div>
                              <p className="font-medium">{formatCurrency(inv.total)}</p>
                              {inv.amount_paid > 0 && inv.amount_paid < inv.total && (
                                <p className="text-xs text-gray-500">
                                  Paid: {formatCurrency(inv.amount_paid)}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatDate(inv.due_date)}</td>
                          <td className="px-6 py-4 text-sm">
                            {inv.stripe_payment_intent_id ? (
                              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                {inv.stripe_payment_intent_id.slice(-12)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => loadInvoiceDetail(inv.id)}
                                className="text-gray-600 hover:text-gray-800 p-1"
                                title="View Invoice"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => downloadInvoicePdf(inv.id)}
                                disabled={downloadingPdf}
                                className="text-gray-600 hover:text-gray-800 p-1"
                                title="Download PDF"
                              >
                                {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                              </button>
                              {inv.status === 'draft' && (
                                <button
                                  onClick={() => handleIssueInvoice(inv.id)}
                                  disabled={processing}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Issue
                                </button>
                              )}
                              {(inv.status === 'issued' || inv.status === 'partially_paid') && (
                                <button
                                  onClick={() => handleChargeInvoice(inv.id)}
                                  disabled={processing}
                                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                                >
                                  Charge
                                </button>
                              )}
                              {inv.status === 'issued' && !inv.xero_invoice_id &&
                               (inv.xero_sync_status === 'pending' || inv.xero_sync_status === 'retrying' || inv.xero_sync_status === 'failed') && (
                                <button
                                  onClick={() => retryInvoiceSync(inv.id)}
                                  disabled={processing}
                                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                                >
                                  Retry Sync
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {billingSubTab === 'receipts' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">All Receipts</h3>
              <button onClick={loadReceipts} className="text-gray-600 hover:text-gray-800 p-2">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {receipts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No receipts found</p>
                <p className="text-sm mt-1">Receipts are generated when payments are processed</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {receipts.map((receipt) => (
                      <tr key={receipt.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {receipt.receipt_number}
                        </td>
                        <td className="px-6 py-4 text-sm">{receipt.organization_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(receipt.paid_at)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatCurrency(receipt.amount)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {receipt.payment_method_brand ? (
                            <span className="capitalize">
                              {receipt.payment_method_brand} ****{receipt.payment_method_last4}
                            </span>
                          ) : (
                            <span>Card</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {receipt.stripe_payment_intent_id ? (
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {receipt.stripe_payment_intent_id.slice(-12)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => downloadReceiptPdf(receipt.id)}
                              disabled={downloadingPdf}
                              className="text-gray-600 hover:text-gray-800 p-1"
                              title="Download PDF"
                            >
                              {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // Settings Section
  // =========================================================================

  const SettingsSection = () => {
    const [selectedAccount, setSelectedAccount] = useState(xeroSettings?.revenue_account_code || '');
    const [selectedTaxType, setSelectedTaxType] = useState(xeroSettings?.tax_type || 'NONE');
    const [selectedBankAccount, setSelectedBankAccount] = useState(xeroSettings?.bank_account_code || '');

    const handleSave = () => {
      const account = xeroAccounts.find(a => a.code === selectedAccount);
      const bankAccount = xeroBankAccounts.find(a => a.code === selectedBankAccount);
      saveXeroSettings(
        selectedAccount,
        account?.name || '',
        selectedTaxType,
        selectedBankAccount,
        bankAccount?.name || ''
      );
    };

    const loadAllAccounts = () => {
      loadXeroAccounts();
      loadXeroBankAccounts();
    };

    return (
      <div className="space-y-6">
        {/* Xero Connection Status */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Xero Integration</h3>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {xeroSettings?.xero_connected ? (
                  <>
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <Link className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">Connected to Xero</p>
                      <p className="text-sm text-gray-500">Invoices will sync automatically</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                      <Unlink className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Not Connected</p>
                      <p className="text-sm text-gray-500">Connect Xero to sync invoices</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pending Syncs */}
        {xeroSettings?.xero_connected && pendingSyncSummary && (pendingSyncSummary.total_pending > 0 || pendingSyncSummary.failed > 0) && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Pending Invoice Syncs</h3>
              <button
                onClick={processPendingSyncs}
                disabled={processingSync || pendingSyncSummary.ready_to_process === 0}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingSync ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Process Now
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-700">{pendingSyncSummary.pending || 0}</p>
                  <p className="text-sm text-yellow-600">Pending</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{pendingSyncSummary.retrying || 0}</p>
                  <p className="text-sm text-blue-600">Retrying</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-700">{pendingSyncSummary.failed || 0}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{pendingSyncSummary.ready_to_process || 0}</p>
                  <p className="text-sm text-green-600">Ready to Process</p>
                </div>
              </div>
              {pendingSyncSummary.next_retry_at && (
                <p className="text-sm text-gray-500">
                  Next scheduled retry: {formatDate(pendingSyncSummary.next_retry_at)}
                </p>
              )}
              {pendingSyncSummary.failed > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">
                        {pendingSyncSummary.failed} invoice(s) failed to sync after multiple attempts
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        Go to the Invoices tab to manually retry individual invoices.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Account Configuration */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Xero Account Configuration</h3>
            <button
              onClick={loadAllAccounts}
              disabled={!xeroSettings?.xero_connected || loadingXeroAccounts || loadingBankAccounts}
              className="flex items-center text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(loadingXeroAccounts || loadingBankAccounts) ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Load Accounts from Xero
            </button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Configure Xero accounts for invoice line items and payment recording.
            </p>

            {/* Current Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Settings</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Revenue Account:</span>
                  <span className="ml-2 font-mono font-medium">{xeroSettings?.revenue_account_code || 'Not set'}</span>
                  {xeroSettings?.revenue_account_name && (
                    <span className="text-gray-500 ml-1">({xeroSettings.revenue_account_name})</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Bank Account:</span>
                  <span className="ml-2 font-mono font-medium">{xeroSettings?.bank_account_code || 'Not set'}</span>
                  {xeroSettings?.bank_account_name && (
                    <span className="text-gray-500 ml-1">({xeroSettings.bank_account_name})</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Tax Type:</span>
                  <span className="ml-2 font-medium">{xeroSettings?.tax_type || 'NONE'}</span>
                </div>
                {xeroSettings?.updated_at && (
                  <div>
                    <span className="text-gray-500">Last Updated:</span>
                    <span className="ml-2">{formatDate(xeroSettings.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Account Selection */}
            {(xeroAccounts.length > 0 || xeroBankAccounts.length > 0) && (
              <div className="space-y-4">
                {xeroAccounts.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Revenue Account (for invoice line items)
                    </label>
                    <select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select an account...</option>
                      {xeroAccounts.map((account) => (
                        <option key={account.code} value={account.code}>
                          {account.code} - {account.name} ({account.type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {xeroBankAccounts.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Account (for payment recording)
                    </label>
                    <select
                      value={selectedBankAccount}
                      onChange={(e) => setSelectedBankAccount(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a bank account...</option>
                      {xeroBankAccounts.map((account) => (
                        <option key={account.code} value={account.code}>
                          {account.code} - {account.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This account will be used when recording payments against invoices in Xero
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Type
                  </label>
                  <select
                    value={selectedTaxType}
                    onChange={(e) => setSelectedTaxType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="NONE">NONE - No Tax</option>
                    <option value="OUTPUT">OUTPUT - Standard Sales Tax</option>
                    <option value="OUTPUT2">OUTPUT2 - Reduced Rate Tax</option>
                    <option value="EXEMPTOUTPUT">EXEMPTOUTPUT - Tax Exempt</option>
                    <option value="ZERORATEDOUTPUT">ZERORATEDOUTPUT - Zero Rated</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Tax type to apply to invoice line items in Xero
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={processing || !selectedAccount}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Save Settings
                  </button>
                </div>
              </div>
            )}

            {!xeroSettings?.xero_connected && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">Xero Not Connected</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Connect to Xero to load available accounts. Until then, the default account codes will be used.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // Credit Notes Section
  // =========================================================================

  const CreditNotesSection = () => {
    const formatCurrency = (amount, currency = 'USD') => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(amount / 100);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const getStatusBadge = (cn) => {
      // Determine status from xero_status (for refunds) or status (for manual)
      const status = cn.xero_status || cn.status;
      const statusConfig = {
        created: { label: 'Created', color: 'bg-green-100 text-green-800' },
        synced: { label: 'Synced', color: 'bg-green-100 text-green-800' },
        pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
        xero_failed: { label: 'Xero Failed', color: 'bg-red-100 text-red-800' },
        applied: { label: 'Applied', color: 'bg-blue-100 text-blue-800' },
      };
      const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
      return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
          {config.label}
        </span>
      );
    };

    const getSourceBadge = (source) => {
      if (source === 'refund') {
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
            Refund
          </span>
        );
      }
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          Manual
        </span>
      );
    };

    return (
      <div className="space-y-6">
        {/* Actions Bar */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Credit Notes</h2>
            <p className="text-sm text-gray-500">View and manage credit notes across all organizations</p>
          </div>
          <button
            onClick={() => {
              setCreditNoteOrg(null);
              setCreditNoteInvoice(null);
              setShowCreditNoteModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create Credit Note
          </button>
        </div>

        {/* Credit Notes Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loadingCreditNotes ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
              <p className="text-gray-500 mt-2">Loading credit notes...</p>
            </div>
          ) : creditNotes.length === 0 ? (
            <div className="p-8 text-center">
              <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No credit notes found</p>
              <p className="text-sm text-gray-400 mt-1">
                Credit notes will appear here when created
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Xero ID
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {creditNotes.map((cn) => (
                  <tr key={cn.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(cn.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSourceBadge(cn.source)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {cn.organization_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(cn.amount, cn.currency)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {cn.reason}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cn.invoice_number ? (
                        <span className="font-mono text-xs">{cn.invoice_number}</span>
                      ) : cn.invoice_id ? (
                        <span className="font-mono text-xs">{cn.invoice_id.slice(0, 8)}...</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(cn)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cn.xero_credit_note_id ? (
                        <span className="font-mono text-xs text-green-600">
                          {cn.xero_credit_note_id.slice(0, 8)}...
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Refunds Section
  // =========================================================================

  const RefundsSection = () => {
    const handleSelectRefund = (refund) => {
      setSelectedRefundDetails(refund);
    };

    const handleRefundProcessed = () => {
      // Trigger refresh of pending refunds
      setRefundsRefreshKey(prev => prev + 1);
      // Reload dashboard data to update pending refunds count
      loadDashboardData();
    };

    return (
      <div className="space-y-6">
        {/* Actions Bar */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              setCreditNoteOrg(null);
              setCreditNoteInvoice(null);
              setShowCreditNoteModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create Credit Note
          </button>
        </div>

        {/* Pending Approvals Section */}
        <RefundApprovalPanel
          key={refundsRefreshKey}
          currentUser={currentUser}
          onRefundProcessed={handleRefundProcessed}
        />

        {/* All Refunds List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">All Refunds</h3>
            <p className="text-sm text-gray-500">View and manage refund requests across all organizations</p>
          </div>
          <div className="p-4">
            <RefundList
              onSelectRefund={handleSelectRefund}
              isAdmin={true}
            />
          </div>
        </div>

        {/* Refund Details Modal */}
        {selectedRefundDetails && (
          <RefundDetails
            refund={selectedRefundDetails}
            onClose={() => setSelectedRefundDetails(null)}
            canApprove={false}
            currentUserId={currentUser?.id}
          />
        )}
      </div>
    );
  };

  // =========================================================================
  // Create Invoice Modal
  // =========================================================================

  const CreateInvoiceModal = () => {
    const [orgId, setOrgId] = useState(createInvoiceOrgId);
    const [formData, setFormData] = useState({
      period_start: '',
      period_end: '',
      due_date: '',
      notes: '',
      issue_immediately: true,
      currency: 'USD',
      line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    });

    const handleCreate = async () => {
      if (!orgId) {
        setError('Please select an organization');
        return;
      }

      try {
        setProcessing(true);
        await axios.post(
          `${getApiBase()}/admin/billing/invoices`,
          {
            period_start: new Date(formData.period_start).toISOString(),
            period_end: new Date(formData.period_end).toISOString(),
            due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
            notes: formData.notes,
            issue_immediately: formData.issue_immediately,
            currency: formData.currency,
            line_items: formData.line_items.map(item => ({
              description: item.description,
              quantity: parseFloat(item.quantity),
              unit_price: Math.round(parseFloat(item.unit_price) * 100),
              tax_amount: 0,
            })),
          },
          {
            headers: getAuthHeaders(),
            params: { organization_id: orgId },
          }
        );

        setSuccess('Invoice created successfully');
        setShowCreateInvoice(false);
        loadInvoices();
        loadDashboardData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to create invoice');
      } finally {
        setProcessing(false);
      }
    };

    const addLineItem = () => {
      setFormData({
        ...formData,
        line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0 }],
      });
    };

    const updateLineItem = (index, field, value) => {
      const updated = [...formData.line_items];
      updated[index] = { ...updated[index], [field]: value };
      setFormData({ ...formData, line_items: updated });
    };

    const removeLineItem = (index) => {
      if (formData.line_items.length > 1) {
        setFormData({
          ...formData,
          line_items: formData.line_items.filter((_, i) => i !== index),
        });
      }
    };

    const calculateTotal = () => {
      return formData.line_items.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      }, 0);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Create Invoice</h3>
            <button onClick={() => setShowCreateInvoice(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Organization Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an organization</option>
                {organizations.map((item) => (
                  <option key={item.organization.id} value={item.organization.id}>
                    {item.organization.name}
                    {!item.billing_profile && ' (No billing profile)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Currency and Dates */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="NZD">NZD - New Zealand Dollar</option>
                  <option value="CHF">CHF - Swiss Franc</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="SGD">SGD - Singapore Dollar</option>
                  <option value="HKD">HKD - Hong Kong Dollar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Start</label>
                <input
                  type="date"
                  value={formData.period_start}
                  onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period End</label>
                <input
                  type="date"
                  value={formData.period_end}
                  onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Line Items</label>
              {formData.line_items.map((item, index) => (
                <div key={index} className="flex gap-3 mb-2">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                    className="w-20 px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    value={item.unit_price}
                    onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                    className="w-28 px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => removeLineItem(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addLineItem}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Line Item
              </button>
            </div>

            {/* Total */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between text-lg font-medium">
                <span>Total ({formData.currency}):</span>
                <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: formData.currency }).format(calculateTotal())}</span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Issue Immediately */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="issue_immediately"
                checked={formData.issue_immediately}
                onChange={(e) => setFormData({ ...formData, issue_immediately: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="issue_immediately" className="ml-2 text-sm text-gray-700">
                Issue immediately (sync to Xero if connected)
              </label>
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <button
              onClick={() => setShowCreateInvoice(false)}
              className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={processing || !orgId || !formData.period_start || !formData.period_end}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Create Invoice
            </button>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // Action Handlers
  // =========================================================================

  const handleChargeInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to charge this invoice to the organization\'s default payment method?')) {
      return;
    }

    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/billing/charge`,
        null,
        {
          headers: getAuthHeaders(),
          params: { invoice_id: invoiceId },
        }
      );
      setSuccess('Invoice charged successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Reload data
      if (orgDetail) {
        loadOrgDetail(orgDetail.organization.id);
      } else {
        loadInvoices();
      }
      loadDashboardData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to charge invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleIssueInvoice = async (invoiceId) => {
    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/billing/invoices/${invoiceId}/issue`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess('Invoice issued successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadInvoices();
      loadDashboardData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to issue invoice');
    } finally {
      setProcessing(false);
    }
  };

  // =========================================================================
  // ACH Billing Section
  // =========================================================================

  const ACHBillingSection = () => {
    const getChargeStatusColor = (status) => {
      const colors = {
        pending: 'bg-yellow-100 text-yellow-800',
        processing: 'bg-blue-100 text-blue-800',
        succeeded: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
      };
      return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const getEntryTypeLabel = (type) => {
      const labels = { travel: 'Travel Booking', subscription: 'Subscription', adjustment: 'Adjustment', credit: 'Credit' };
      return labels[type] || type;
    };

    const getTriggerLabel = (trigger) => {
      const labels = { booking: 'At Booking', monthly_schedule: 'Monthly', threshold_reached: 'Threshold', manual: 'Manual' };
      return labels[trigger] || trigger;
    };

    const getBillingModeColor = (mode) => {
      const colors = {
        monthly: 'bg-blue-100 text-blue-800',
        threshold: 'bg-purple-100 text-purple-800',
        monthly_and_threshold: 'bg-indigo-100 text-indigo-800',
      };
      return colors[mode] || 'bg-gray-100 text-gray-800';
    };

    if (loadingAch) {
      return (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      );
    }

    // If viewing a specific org's ACH detail
    if (achSelectedOrg) {
      return (
        <div className="space-y-6">
          {/* Back button */}
          <button
            onClick={() => { setAchSelectedOrg(null); setAchOrgBalance(null); setAchOrgEntries([]); setAchOrgCharges([]); }}
            className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to ACH Overview
          </button>

          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {achSelectedOrg.organization_name || achSelectedOrg.organization_id}
            </h2>
            <button
              onClick={() => loadAchOrgDetail(achSelectedOrg.organization_id)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </button>
          </div>

          {loadingAchOrg ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Balance Summary */}
              {achOrgBalance && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm text-gray-500">Current Balance</p>
                    <p className="text-2xl font-semibold">{formatCurrency(achOrgBalance.current_balance_cents || 0)}</p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm text-gray-500">Billing Mode</p>
                    <p className="text-lg font-semibold capitalize">{(achOrgBalance.billing_mode || '').replace(/_/g, ' ')}</p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm text-gray-500">Next Charge Date</p>
                    <p className="text-lg font-semibold">
                      {achOrgBalance.next_monthly_charge_date ? formatDate(achOrgBalance.next_monthly_charge_date) : 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Balance Entries */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">Balance Entries</h3>
                </div>
                <div className="overflow-x-auto">
                  {achOrgEntries.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No balance entries</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {achOrgEntries.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(entry.created_at)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                entry.entry_type === 'travel' ? 'bg-indigo-100 text-indigo-800' :
                                entry.entry_type === 'subscription' ? 'bg-blue-100 text-blue-800' :
                                entry.entry_type === 'credit' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {getEntryTypeLabel(entry.entry_type)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{entry.description || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">{formatCurrency(entry.amount || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                entry.settled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {entry.settled ? 'Settled' : 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Charge History */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">Charge History</h3>
                </div>
                <div className="overflow-x-auto">
                  {achOrgCharges.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No charges yet</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {achOrgCharges.map((charge) => (
                          <tr key={charge.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(charge.created_at)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{getTriggerLabel(charge.trigger)}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{charge.description || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">{formatCurrency(charge.amount || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChargeStatusColor(charge.status)}`}>
                                {(charge.status || '').replace(/_/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // ACH Overview — summary + org list
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        {achSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active ACH Configs</p>
              <p className="text-2xl font-semibold">{achSummary.active_configs || 0}</p>
              <p className="text-xs text-gray-400">{achSummary.total_configs || 0} total</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Outstanding Balance</p>
              <p className="text-2xl font-semibold">{formatCurrency(achSummary.total_outstanding_balance_cents || 0)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Monthly Billing</p>
              <p className="text-2xl font-semibold">{achSummary.monthly_billing_count || 0}</p>
              <p className="text-xs text-gray-400">organizations</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Charges Today</p>
              <p className="text-2xl font-semibold">{achSummary.charges_today || 0}</p>
            </div>
          </div>
        )}

        {/* ACH Org Configs List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">ACH Billing Organizations</h3>
            <button
              onClick={loadAchSummary}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            {achConfigs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No ACH billing configurations found</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing Mode</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Charge</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {achConfigs.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {config.organization_name || config.organization_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBillingModeColor(config.billing_mode)}`}>
                          {(config.billing_mode || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        {formatCurrency(config.current_balance_cents || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {config.next_monthly_charge_date ? formatDate(config.next_monthly_charge_date) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.status === 'active' ? 'bg-green-100 text-green-800' :
                          config.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {config.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            setAchSelectedOrg({ organization_id: config.organization_id, organization_name: config.organization_name || config.organization_id });
                            loadAchOrgDetail(config.organization_id);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <Eye className="w-4 h-4 inline mr-1" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // Main Render
  // =========================================================================

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'organizations', label: 'Organizations', icon: Building2 },
    { id: 'subscriptions', label: 'Subscriptions', icon: Repeat },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'credit-notes', label: 'Credit Notes', icon: Receipt },
    { id: 'ach-billing', label: 'ACH Billing', icon: Landmark },
    { id: 'refunds', label: 'Refunds', icon: RotateCcw },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="max-w-7xl mx-auto bg-gray-50 min-h-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Billing</h1>
        <p className="text-gray-600">Manage billing across all organizations</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-800">
          <Check className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedOrg(null); setOrgDetail(null); }}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardSection />}
      {activeTab === 'organizations' && renderOrganizationsSection()}
      {activeTab === 'subscriptions' && <SubscriptionSection currentUser={currentUser} />}
      {activeTab === 'invoices' && <InvoicesSection />}
      {activeTab === 'credit-notes' && <CreditNotesSection />}
      {activeTab === 'ach-billing' && <ACHBillingSection />}
      {activeTab === 'refunds' && <RefundsSection />}
      {activeTab === 'settings' && <SettingsSection />}

      {/* Create Invoice Modal */}
      {showCreateInvoice && <CreateInvoiceModal />}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setSelectedInvoice(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Invoice {selectedInvoice.invoice_number || selectedInvoice.provisional_number}
                  </h2>
                  <p className="text-sm text-gray-500">{selectedInvoice.organization?.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadInvoicePdf(selectedInvoice.id)}
                    disabled={downloadingPdf}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {downloadingPdf ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download PDF
                  </button>
                  <button
                    onClick={() => setSelectedInvoice(null)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                {loadingInvoiceDetail ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Status and Dates */}
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedInvoice.status)}`}>
                          {selectedInvoice.status?.replace('_', ' ').toUpperCase()}
                        </span>
                        {selectedInvoice.invoice_number_source && (
                          <span className={`ml-2 px-2 py-1 text-xs rounded ${
                            selectedInvoice.invoice_number_source === 'xero'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {selectedInvoice.invoice_number_source === 'xero' ? 'Xero Invoice' : 'App Invoice'}
                          </span>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        <p><span className="text-gray-500">Issue Date:</span> {formatDate(selectedInvoice.issue_date)}</p>
                        <p><span className="text-gray-500">Due Date:</span> {formatDate(selectedInvoice.due_date)}</p>
                      </div>
                    </div>

                    {/* Billing Info */}
                    <div className="grid grid-cols-2 gap-6 bg-gray-50 rounded-lg p-4">
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Bill To</h4>
                        <p className="font-medium">{selectedInvoice.billing_profile?.company_name}</p>
                        {selectedInvoice.billing_profile?.billing_address && (
                          <>
                            <p className="text-sm text-gray-600">{selectedInvoice.billing_profile.billing_address.line1}</p>
                            {selectedInvoice.billing_profile.billing_address.line2 && (
                              <p className="text-sm text-gray-600">{selectedInvoice.billing_profile.billing_address.line2}</p>
                            )}
                            <p className="text-sm text-gray-600">
                              {selectedInvoice.billing_profile.billing_address.city}, {selectedInvoice.billing_profile.billing_address.state} {selectedInvoice.billing_profile.billing_address.postal_code}
                            </p>
                          </>
                        )}
                        <p className="text-sm text-gray-600">{selectedInvoice.billing_profile?.billing_email}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Invoice Period</h4>
                        <p className="text-sm">
                          {formatDate(selectedInvoice.period_start)} - {formatDate(selectedInvoice.period_end)}
                        </p>
                        {selectedInvoice.xero_invoice_id && (
                          <div className="mt-2">
                            <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Xero Invoice ID</h4>
                            <p className="text-xs font-mono bg-white px-2 py-1 rounded">{selectedInvoice.xero_invoice_id}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Line Items */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Line Items</h4>
                      <table className="min-w-full divide-y divide-gray-200 border rounded-lg overflow-hidden">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedInvoice.line_items?.map((item, index) => (
                            <tr key={item.id || index}>
                              <td className="px-4 py-3 text-sm">{item.description}</td>
                              <td className="px-4 py-3 text-sm text-center">{item.quantity}</td>
                              <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.unit_price, selectedInvoice.currency)}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(item.amount, selectedInvoice.currency)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subtotal:</span>
                          <span>{formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                        </div>
                        {selectedInvoice.tax_amount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Tax:</span>
                            <span>{formatCurrency(selectedInvoice.tax_amount, selectedInvoice.currency)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-base font-semibold pt-2 border-t">
                          <span>Total:</span>
                          <span>{formatCurrency(selectedInvoice.total, selectedInvoice.currency)}</span>
                        </div>
                        {selectedInvoice.amount_paid > 0 && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Paid:</span>
                            <span>-{formatCurrency(selectedInvoice.amount_paid, selectedInvoice.currency)}</span>
                          </div>
                        )}
                        {selectedInvoice.status !== 'paid' && selectedInvoice.total - selectedInvoice.amount_paid > 0 && (
                          <div className="flex justify-between text-base font-semibold text-blue-600 pt-2 border-t">
                            <span>Amount Due:</span>
                            <span>{formatCurrency(selectedInvoice.total - selectedInvoice.amount_paid, selectedInvoice.currency)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payments */}
                    {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Payment History</h4>
                        <div className="bg-green-50 border border-green-200 rounded-lg divide-y divide-green-200">
                          {selectedInvoice.payments.map((payment) => (
                            <div key={payment.id} className="px-4 py-3 flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-green-800">
                                  {formatCurrency(payment.amount, payment.currency)} - {payment.status}
                                </p>
                                <p className="text-xs text-green-600">{formatDate(payment.processed_at || payment.created_at)}</p>
                              </div>
                              {payment.stripe_payment_intent_id && (
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Stripe Reference</p>
                                  <p className="font-mono text-xs bg-white px-2 py-1 rounded">
                                    {payment.stripe_payment_intent_id}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Refunds */}
                    {selectedInvoice.refunds && selectedInvoice.refunds.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Refund History</h4>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg divide-y divide-orange-200">
                          {selectedInvoice.refunds.map((refund) => (
                            <div key={refund.id} className="px-4 py-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium text-orange-800">
                                    {formatCurrency(refund.amount, refund.currency || 'USD')} - {refund.status}
                                  </p>
                                  <p className="text-xs text-orange-600">
                                    {formatDate(refund.processed_at || refund.created_at)}
                                  </p>
                                  {refund.reason && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Reason: {refund.reason}
                                      {refund.reason_notes && ` - ${refund.reason_notes}`}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right space-y-1">
                                  {refund.stripe_refund_id && (
                                    <div>
                                      <p className="text-xs text-gray-500">Stripe Refund</p>
                                      <p className="font-mono text-xs bg-white px-2 py-1 rounded">
                                        {refund.stripe_refund_id}
                                      </p>
                                    </div>
                                  )}
                                  {refund.xero_credit_note_id && (
                                    <div>
                                      <p className="text-xs text-gray-500">Xero Credit Note</p>
                                      <p className="font-mono text-xs bg-white px-2 py-1 rounded text-green-600">
                                        {refund.xero_credit_note_id}
                                      </p>
                                    </div>
                                  )}
                                  {refund.credit_note_status === 'failed' && (
                                    <div>
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                        Credit Note Failed
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedInvoice.notes && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedInvoice.notes}</p>
                      </div>
                    )}

                    {/* Sync Status */}
                    {selectedInvoice.xero_sync_status && selectedInvoice.xero_sync_status !== 'synced' && (
                      <div className={`p-4 rounded-lg ${
                        selectedInvoice.xero_sync_status === 'failed'
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-yellow-50 border border-yellow-200'
                      }`}>
                        <div className="flex items-start">
                          <AlertCircle className={`w-5 h-5 mr-3 ${
                            selectedInvoice.xero_sync_status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                          }`} />
                          <div>
                            <p className={`font-medium ${
                              selectedInvoice.xero_sync_status === 'failed' ? 'text-red-800' : 'text-yellow-800'
                            }`}>
                              Xero Sync: {selectedInvoice.xero_sync_status}
                            </p>
                            {selectedInvoice.xero_last_error && (
                              <p className="text-sm text-gray-600 mt-1">{selectedInvoice.xero_last_error}</p>
                            )}
                            {selectedInvoice.xero_sync_attempts > 0 && (
                              <p className="text-xs text-gray-500 mt-1">Attempts: {selectedInvoice.xero_sync_attempts}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions for Paid Invoices */}
                    {selectedInvoice.status === 'paid' && (
                      <div className="border-t pt-4 mt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Actions</h4>
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setAdminRefundInvoice(selectedInvoice);
                              setShowAdminRefundModal(true);
                              setSelectedInvoice(null);
                            }}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Issue Refund
                          </button>
                          <button
                            onClick={() => {
                              setCreditNoteOrg(selectedInvoice.organization);
                              setCreditNoteInvoice(selectedInvoice);
                              setShowCreditNoteModal(true);
                              setSelectedInvoice(null);
                            }}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                          >
                            <FileText className="w-4 h-4" />
                            Issue Credit Note
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Refund Modal */}
      <AdminRefundModal
        isOpen={showAdminRefundModal}
        onClose={() => {
          setShowAdminRefundModal(false);
          setAdminRefundInvoice(null);
        }}
        invoice={adminRefundInvoice}
        onRefundCreated={() => {
          loadInvoices();
          setRefundsRefreshKey(prev => prev + 1);
          setActiveTab('refunds');
        }}
      />

      {/* Credit Note Modal */}
      <CreditNoteModal
        isOpen={showCreditNoteModal}
        onClose={() => {
          setShowCreditNoteModal(false);
          setCreditNoteOrg(null);
          setCreditNoteInvoice(null);
        }}
        organization={creditNoteOrg}
        invoice={creditNoteInvoice}
        onCreditNoteCreated={() => {
          loadInvoices();
          loadCreditNotes();
        }}
      />
    </div>
  );
};

export default AdminBillingDashboard;
